import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AlertTriangle, X, Trophy } from "lucide-react";
import { TIER_LABELS } from "@/data/options";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

function CarrierDropdown({ carriers, value, onChange, testid }) {
  const grouped = { 1: [], 2: [], 3: [], 0: [] };
  carriers.forEach((c) => grouped[c.tier]?.push(c));
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testid}>
        <SelectValue placeholder="Choose a carrier…" />
      </SelectTrigger>
      <SelectContent className="max-h-[340px]">
        {[1, 2, 3, 0].map((tier) =>
          grouped[tier].length ? (
            <SelectGroup key={tier}>
              <SelectLabel className="text-[#F97316] text-xs uppercase tracking-wide">
                {TIER_LABELS[tier]}
              </SelectLabel>
              {grouped[tier].map((c) => (
                <SelectItem key={c.id} value={c.id} disabled={c.disabled}>
                  {c.name}
                  {c.location ? ` — ${c.location}` : ""}
                  {c.disabled ? " (Do Not Use)" : ""}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null
        )}
      </SelectContent>
    </Select>
  );
}

// One carrier bid row: carrier picker + quoted rate, highlighted when it's the lowest bid.
export const BidRow = ({ bid, index, carriers, isLowest, canRemove, onUpdate, onRemove }) => {
  const selected = carriers.find((c) => c.id === bid.carrierId);
  return (
    <div
      className={`rounded-md border p-3 space-y-2 ${
        isLowest ? "border-[#16A34A] bg-[#F0FDF4]" : "border-border"
      }`}
      data-testid={`carrier-bid-row-${index}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#57534E]">
          Bid {index + 1}
        </span>
        <div className="flex items-center gap-2">
          {isLowest && (
            <span
              className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#16A34A] bg-[#DCFCE7] px-2 py-0.5 rounded"
              data-testid="lowest-bid-badge"
            >
              <Trophy className="h-3 w-3" /> Lowest bid
            </span>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-stone-400 hover:text-red-600"
              data-testid={`remove-carrier-bid-${index}`}
              aria-label="Remove bid"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px] gap-2">
        <CarrierDropdown
          carriers={carriers}
          value={bid.carrierId}
          onChange={(id) =>
            onUpdate({ carrierId: id, carrierName: carriers.find((c) => c.id === id)?.name || "" })
          }
          testid={`carrier-bid-select-${index}`}
        />
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#57534E]">$</span>
          <Input
            type="number"
            value={bid.rate}
            onChange={(e) => onUpdate({ rate: e.target.value })}
            placeholder="Quote"
            className={`pl-7 ${inputCls}`}
            data-testid={`carrier-bid-rate-${index}`}
          />
        </div>
      </div>
      {selected?.disabled && (
        <p className="flex items-center gap-1 text-xs text-red-700" data-testid={`carrier-bid-warning-${index}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> {selected.notes || "Marked Do Not Use"}
        </p>
      )}
    </div>
  );
};
